import { defineComponent as _defineComponent } from 'vue'

import {
  computed,
  defineComponent,
  onMounted,
  ref,
  ssrRef,
  useAsync,
  useContext,
  useMeta,
  useRoute,
  useStore,
} from "@nuxtjs/composition-api";

import { useAmplitude, useMetaService, useTranslate } from "@/composables";
import { getAmplitudeStore } from "@/store/amplitude";
import { getExperimentsStore } from "@/store/experiments";
import { getProductSetsStore } from "@/store/product-sets";
import { getProfilesStore } from "@/store/profiles";
import { formatErrorMessage, handleErrorStatusCode, handleServiceError } from "@/utils/api";
import { NOT_FOUND } from "@/utils/enums/http-codes";
import { EXPERIMENTS_CREATOR_GIFT_GUIDES } from "@/utils/experiments/constants";
import logger, { logLevel } from "@/utils/logger";

const __default__ = defineComponent({
  name: "ProductSets",
  head: {},
});

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props) {

const context = useContext();
const route = useRoute();
const store = useStore();
const amplitude = useAmplitude();
const { t } = useTranslate();
const metaService = useMetaService();

const profilesStore = getProfilesStore(store);
const productSetStore = getProductSetsStore(store);

const displayName = ref(route.value.params.displayName);

const pageTitle = t("{influencerName} Product Sets on LTK", { influencerName: displayName.value }); // Title text on the top of a creator's product sets page
const detailHeaderText = t("My Products"); // Header text on the top of a creator's product sets page

const paginate = ssrRef(false, "paginate");
const pending = ssrRef(false, "pending");
const error = ref<unknown | null>(null);
const ltkFetchState = computed(() => ({ error: error.value, pending: pending.value }));

useAsync(async () => {
  try {
    pending.value = true;
    if (!profilesStore.hasProfileBeenFetchedByName(displayName.value)) {
      await profilesStore.fetchProfileByDisplayName(displayName.value);
    }

    const profileId = profilesStore.getIdByDisplayName(displayName.value) || "";

    if (!profileId) {
      logger(logLevel.ERROR, "Profile ID not found while loading productsets route");
      return context.error({ statusCode: NOT_FOUND, message: "Profile not found" });
    }

    if (!productSetStore.hasFeedBeenFetched(displayName.value)) {
      const experimentsStore = getExperimentsStore(store);
      experimentsStore.fetchFeatureFlag({ flagName: EXPERIMENTS_CREATOR_GIFT_GUIDES });
      const giftGuideFF = experimentsStore.flag(EXPERIMENTS_CREATOR_GIFT_GUIDES).value === "on";
      await productSetStore.fetchProductSetsByProfile({ displayName: displayName.value, profileId, giftGuideFF });
    }
    pending.value = false;
  } catch (err) {
    pending.value = false;
    error.value = err;
    logger(logLevel.ERROR, "Error fetching creator product sets", {}, err);
    context.error({
      statusCode: handleErrorStatusCode(err),
      message: `Error fetching creator product sets (${route.value.params.displayName}) - ${formatErrorMessage(err)}`,
    });
  }
  paginate.value = true;
}, "fetch");

const fetch = async () => {
  if (profileId.value && (paginate || !productSetStore.hasFeedBeenFetched(displayName.value))) {
    try {
      pending.value = true;
      await productSetStore.fetchProductSetsByProfile({ displayName: displayName.value, profileId: profileId.value });
      pending.value = false;
    } catch (err) {
      pending.value = false;
      error.value = error;
      return handleServiceError({
        context,
        error: {
          status: handleErrorStatusCode(err),
          errorCode: "creator-productsets-not-found",
          developerMessage: `Error fetching creator producsets (${displayName})`,
        },
        originalError: err,
        meta: { url: route.value.fullPath },
      });
    }
  }
  paginate.value = true;
};

onMounted(() => {
  amplitude?.sendEvent("/page/product-sets", {
    "gift-guide": false,
  });
});

const onPaging = () => {
  fetch();
};

const hasNextPage = computed(() => {
  return productSetStore.getFeedHasMorePages(displayName.value);
});

const productSets = computed(() => {
  return productSetStore.productSetFeed(displayName.value).ids;
});

const profileId = computed(() => {
  return profilesStore.getIdByDisplayName(displayName.value);
});

const productSetEventProps = computed(() => {
  return {
    "gift-guide": false,
    "publisher-id": profileId.value,
  };
});

const onProductSetClick = () => {
  getAmplitudeStore(store).setReferringComponent("creator shop product set listing page");
};

const profileAvatar = computed(() => {
  return profilesStore.profiles[profileId.value ?? ""]?.avatarUrl;
});

useMeta(() => {
  const translationParams = { displayName: displayName.value };

  const title = t("Shop {displayName}'s Product Sets on LTK", translationParams); // Meta title
  const description = t("Shop the curated products in these Product Sets from {displayName} on LTK", translationParams); // Meta description

  const head = metaService.setPageMeta({ title: title.value, customTitle: true, description: description.value });

  if (profileAvatar.value) {
    const height = 400;
    const width = 400;
    const image = `${profileAvatar.value}?fit=crop&w=${width}&h=${height}`;
    head.meta?.push(...context.$metaService.setOGImg(image, width));
  }

  return head;
});

return { __sfc: true,context, route, store, amplitude, t, metaService, profilesStore, productSetStore, displayName, pageTitle, detailHeaderText, paginate, pending, error, ltkFetchState, fetch, onPaging, hasNextPage, productSets, profileId, productSetEventProps, onProductSetClick, profileAvatar }
}

})